import React, { useEffect, useState } from 'react';
import { useInterval } from 'utils/hooks/useInterval';
import { jwtSessionLogger } from '@enterprise/common';
import { styled, Typography } from '@beeline.com/bp-design-system';
import { useHeaderIntl } from './translations/useHeaderIntl';
import { useBpIntlProviderContext } from '@beeline.com/bp-intl';

const formatMessage = (secondsLeft: number, intl: ReturnType<typeof useHeaderIntl>, currentLanguage: string) => {
  const relativeTimeFormat = new Intl.RelativeTimeFormat(currentLanguage, {
    style: 'long',
    localeMatcher: 'best fit',
    numeric: 'auto'
  });
  if (secondsLeft == null || Number.isNaN(secondsLeft)) {
    return intl.tokenRefreshTimeUnknown;
  }
  if (secondsLeft < 0) {
    return intl.tokenRefreshedOnNextRequest;
  }
  if (secondsLeft <= 60) {
    return intl.tokenRefreshInOneMinute;
  }
  if (secondsLeft < 3600) {
    return intl.tokenRefreshInRelativeTime({
      relativeTime: relativeTimeFormat.format(Math.ceil(secondsLeft / 60), 'minutes')
    });
  }
  const hours = Math.ceil(secondsLeft / 3600);
  return intl.tokenRefreshInRelativeTime({
    relativeTime: relativeTimeFormat.format(hours, 'hours')
  });
};

export function AuthSessionInfo() {
  const [info, setInfo] = useState<string>('');
  const intl = useHeaderIntl();
  const { currentLang } = useBpIntlProviderContext();

  useEffect(() => {
    setInfo(formatMessage(jwtSessionLogger.howMuchTimeLeftInSeconds(), intl, currentLang));
    jwtSessionLogger.addListener('set', 'authSessionInfo', (timeLeft) => {
      setInfo(formatMessage(timeLeft, intl, currentLang));
    });

    return () => {
      jwtSessionLogger.removeListener('set', 'authSessionInfo');
    };
  }, [intl, currentLang]);

  useInterval(() => {
    const time = jwtSessionLogger.howMuchTimeLeftInSeconds();
    setInfo(formatMessage(time, intl, currentLang));
  }, 60000);
  return <StyledTypography variant="hint">{info}</StyledTypography>;
}

const StyledTypography = styled(Typography)`
    opacity: 0.3;
    align-content: center;
`;
